/* eslint-disable require-jsdoc */
// @flow

import { jitsiLocalStorage } from '@jitsi/js-utils';
import { participantUpdated } from '../../react/features/base/participants/actions';
import { PARTICIPANT_ROLE_CHANGED } from '../../react/features/base/participants/actionTypes';
import { PARTICIPANT_ROLE } from '../../react/features/base/participants/constants';
import { getParticipantById } from '../../react/features/base/participants/functions';
import { DEFAULT_SERVER_HOST, DEFAULT_SERVER_URL } from '../../react/features/base/settings/constants';
import $ from 'jquery';
import RandomUtil from 'lib-jitsi-meet/dist/esm/modules/util/RandomUtil';
import { XMPPEvents } from 'lib-jitsi-meet/dist/esm/service/xmpp/XMPPEvents';

import { CONFIG_CONST } from '../../w-config/constants';


type XmppCredentialRequest = {
    host: string,
    room: string,
    extension: string,
    password: string,
}

type XmppCredentials = {
    xmppLogin: string,
    xmppPassword: string,
    owner: string,
    room: string,
    subject: string
}

type PbxPersonalInfo = { name: string, serial: string } | null;

type XmppCredentialsResult = { credentials: ?XmppCredentials, error: ?string };

async function parseXmppCredentials(response: Response): Promise<XmppCredentialsResult> {
    let credentials = null;

    const responseJson = await response.json();


    if (responseJson.type === 'error') {
        return {
            credentials: null,
            error: responseJson.reason
        };
    } else if (responseJson.result) {
        if (Object.keys(responseJson.result).length === 0) {
            return {
                credentials: null,
                error: 'Can\'t get credentials data'
            };
        }

        credentials = {
            xmppLogin: `${responseJson.result.login}@${CONFIG_CONST.HOSTS_DOMAIN}`,
            xmppPassword: responseJson.result.password,
            owner: responseJson.result.owner,
            room: responseJson.result.room,
            subject: responseJson.result.subject,
            roomPin: responseJson.result.roomPin,
            userRegion: responseJson.result.userRegion
        };
    }

    return {
        credentials,
        error: null
    };
}

/**
 * Retrives XMPP credentials.
 *
 * @param {XmppCredentialRequest} request - The request.
 * @returns {Promise} XMPP credentials.
 */
export async function getXmppCredentials(request: XmppCredentialRequest): Promise<XmppCredentials | null> {
    if (!await loginToPbx(request.host, request.extension, request.password)) {
        return null;
    }

    const info = await getPersonalInfo(request.host);

    if (info === null) {
        return null;
    }

    const token = await getConferenceToken(request.host);

    if (token === null) {
        return null;
    }

    const uri
        = `${DEFAULT_SERVER_URL}/api/v1/Credentials/?`
        + `room=${request.room}&name=${encodeURIComponent(info.name).replace('%20', '+')}`
        + `&extension=${request.extension}&serial=${info.serial}`;

    let credentials = null;

    try {
        const response = await fetch(uri, {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        credentials = await parseXmppCredentials(response);
    } catch (error) {
        console.error(error);
    }

    return credentials;
}

export async function getAnonymousXmppCredentials(room: string, displayName: string): Promise<XmppCredentialsResult> {
    const uri
        = `${DEFAULT_SERVER_URL}/api/v1/Credentials/?`
        + `room=${room}&name=${encodeURIComponent(displayName || 'Unknown')}`;

    let result = null;

    try {
        const response = await fetch(uri);

        result = await parseXmppCredentials(response);
    } catch (error) {
        console.error(error);

        return {
            credentials: null,
            error: 'No connection'
        };
    }

    return result;
}

/**
 * Overrides XMPP credentials.
 *
 * @param {XmppCredentials} credentials - The XMPP credentials.
 * @returns {void}
 */
export function overrideXmppCredentials(credentials: XmppCredentials): void {
    const { xmppLogin, xmppPassword } = credentials;

    jitsiLocalStorage.setItem('xmpp_username_override', `${xmppLogin}/${getUniqIdFromStore(xmppLogin)}`);
    jitsiLocalStorage.setItem('xmpp_password_override', xmppPassword);
}

export function getUniqIdFromStore(userID) {
    const currentStorage = navigator.product === 'ReactNative' ? jitsiLocalStorage : localStorage;

    if (currentStorage.getItem(`${userID}_nick`)) {
        return currentStorage.getItem(`${userID}_nick`);
    }
    const randomNick = RandomUtil.randomHexString(8).toLowerCase();

    currentStorage.setItem(`${userID}_nick`, randomNick);

    return randomNick;
}

/**
 * Login into PBX.
 *
 * @param {string} host - The PBX host.
 * @param {string} login - PBX user login.
 * @param {string} password - PBX user password.
 * @returns {Promise} - 'true' if login is succeed.
 */
async function loginToPbx(host: string, login: string, password: string): Promise<boolean> {
    let responseJson = null;

    try {
        const response = await fetch(`https://${host}/api/v1/personal/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ login,
                password })
        });

        responseJson = await response.json();
    } catch (error) {
        console.error(error);
    }

    return responseJson === null ? false : responseJson.type !== 'error';
}

/**
 * Returns token for 'auth.wildix.com' service.
 *
 * @param {string} host - PBX host.
 * @returns {Promise<string>} - The token.
 */
async function getPersonalToken(host: string): Promise<string | null> {
    let token = null;

    try {
        const response = await fetch(`https://${host}/api/v1/personal/token/?type=JWT&service=auth.wildix.com`);
        const responseJson = await response.json();

        if (responseJson && responseJson.type === 'result') {
            token = responseJson.result.token;
        }
    } catch (error) {
        console.error(error);
    }

    return token;
}

/**
 * Returns token for 'auth.wildix.com' service.
 *
 * @param {string} host - PBX host.
 * @returns {Promise<string>} - The token.
 */
async function getAuthToken(host: string): Promise<string | null> {
    let token = null;

    try {
        const response = await fetch(`https://${host}/auth/token?type=JWT&service=auth.wildix.com`);
        const responseJson = await response.json();

        if (responseJson && responseJson.type === 'result') {
            token = responseJson.result.token;
        }
    } catch (error) {
        console.error(error);
    }

    return token;
}

/**
 * Returns token for 'conference.wildix.com' service.
 *
 * @param {string} host - PBX host.
 * @returns {Promise<string>} - The token.
 */
export async function getFileStorage(): Promise<string | null> {
    let token = null;

    try {
        const authToken = await getAuthToken(DEFAULT_SERVER_HOST);

        if (authToken !== null) {
            const response = await fetch('https://auth.wildix.com/api/v2/Token/FileStorage', {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            const result = await response.json();

            if (result && result.type === 'result') {
                token = result.result.token;
            }
        }
    } catch (error) {
        console.error(error);
    }

    return token;
}

/**
 * Returns token for 'conference.wildix.com' service.
 *
 * @param {string} host - PBX host.
 * @returns {Promise<string>} - The token.
 */
async function getConferenceToken(host: string): Promise<string | null> {
    let token = null;

    try {
        const authToken = await getPersonalToken(host);

        if (authToken !== null) {
            const response = await fetch('https://auth.wildix.com/api/v2/Token/Conference', {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            const responseJson = await response.json();

            if (responseJson && responseJson.type === 'result') {
                token = responseJson.result.token;
            }
        }
    } catch (error) {
        console.error(error);
    }

    return token;
}

/**
 * Parses pbxDn record for it's serial.
 *
 * @param {string} dn - PBX DN.
 * @returns {string} - Serial.
 */
function parseSerial(dn: string): string {
    const [ id ] = dn.split(',');
    // eslint-disable-next-line no-unused-vars
    const [ _, serial ] = id.split('=');

    return serial;
}

/**
 * Returns user personal info.
 *
 * @param {string} host - PBX host.
 * @returns {Promise<PbxPersonalInfo>} - The personal info.
 */
async function getPersonalInfo(host: string): Promise<PbxPersonalInfo | null> {
    let info: PbxPersonalInfo = null;

    try {
        const response = await fetch(`https://${host}/api/v1/Personal/Info/?fields=name,pbxDn`);
        const responseJson = await response.json();

        if (responseJson && responseJson.type === 'result') {
            info = {
                name: responseJson.result.name,
                serial: parseSerial(responseJson.result.pbxDn)
            };
        }
    } catch (error) {
        console.error(error);
    }

    return info;
}


export function parseJwtToken(token): void {
    if (!token) {
        return { exp: 0 };
    }
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('')
    .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
    .join(''));

    return JSON.parse(jsonPayload);
}

export function isTokenExpired(token) {
    return parseJwtToken(token).exp < new Date().getTime() / 1000;
}

export function isTokenExpiredByDate(dateMs = 0) {
    return dateMs < new Date().getTime() / 1000;
}

export function isRoomIdDifferent(stateRoom, urlRoom) {
    return (typeof stateRoom === 'undefined' || typeof urlRoom === 'undefined') || stateRoom !== urlRoom;
}

// eslint-disable-next-line max-params
export function kickUserIfSameJidAndDifferentResource(pres, jid, stropheResJid, member, chatRoom) {
    const isSelfPresence = $(pres).find(
            '>x[xmlns="http://jabber.org/protocol/muc#user"]>'
            + 'status[code="110"]').length;
    const memberJid = jid?.split('@')[0];
    const memberResource = jid?.split('/')[1];
    let myJid = '';
    let myResource = '';

    if (navigator.product === 'ReactNative') {
        const userFullJid = jitsiLocalStorage.getItem('xmpp_username_override');

        myJid = userFullJid?.split('@')[0];
        myResource = userFullJid?.split('/')[1];
    } else {
        myJid = config.websocketUser;
        myResource = config.nick;
    }
    if (!isSelfPresence && chatRoom.joined && myJid === memberJid && memberResource !== myResource) {
        chatRoom.eventEmitter.emit(
            XMPPEvents.KICKED,
            true,
            jid,
            stropheResJid,
            'User in the same room',
            member.isReplaceParticipant);
    }
}

export function sendFormData({ url, data, token }) {
    const headers = {};
    const formData = new FormData();

    headers['Content-Type'] = 'multipart/form-data';
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    // eslint-disable-next-line
    for (const prop in data) {
        formData.append(prop, data[prop]);
    }

    return fetch(url, {
        method: 'POST',
        headers,
        body: formData
    });
}

export function updateParticipantRole({ id, role }) {
    return (dispatch, getState) => {
        const oldParticipantRole = getParticipantById(getState(), id)?.role;
        const { pbxSerial } = getState()['features/base/settings'] || {};
        let newRole = role;

        if (navigator.product === 'ReactNative') {
            newRole = pbxSerial ? PARTICIPANT_ROLE.MODERATOR : PARTICIPANT_ROLE.PARTICIPANT;
        }

        dispatch(participantUpdated({
            id,
            role: newRole
        }));

        if (oldParticipantRole !== newRole) {
            dispatch({
                type: PARTICIPANT_ROLE_CHANGED,
                participant: {
                    id,
                    role: newRole
                }
            });
        }
    };
}
